import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { supabase } from './supabaseClient';

import WordInput from './WordInput';
import AuthForm from './AuthForm';
import ResetPassword from './ResetPassword';
import QuizMode from './QuizMode';

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userId, setUserId] = useState('');

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) return;
        if (user) {
          setIsLoggedIn(true);
          setUserEmail(user.email);
          setUserId(user.id);
        }
      } catch (err) {}
    };
    checkSession();
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        setIsLoggedIn(true);
        setUserEmail(session.user.email);
        setUserId(session.user.id);
      } else if (event === 'SIGNED_OUT') {
        setIsLoggedIn(false);
        setUserEmail('');
        setUserId('');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const addWord = async (word, meaning, distractors = []) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user is logged in');

      const wordData = {
        word,
        meaning,
        user_id: user.id,
        distractors: distractors && distractors.length > 0 ? distractors : null,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase.from('words').insert([wordData]);

      if (error) {
        throw new Error(error.message);
      } else {
        return data;
      }
    } catch (err) {
      throw err;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (!error) {
        setIsLoggedIn(false);
        setUserEmail('');
        setUserId('');
        window.location.href = '/';
      }
    } catch (err) {}
  };

  const getMeaning = async (word) => {
    try {
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`);
      if (!response.ok) throw new Error('Failed to fetch the word meaning');
      const data = await response.json();
      const definitions = data[0].meanings.map((meaning) => ({
        partOfSpeech: meaning.partOfSpeech,
        definition: meaning.definitions[0].definition,
      }));
      return definitions;
    } catch (error) {
      return null;
    }
  };

  return (
    <div className="min-h-screen bg-stone-100">

      {/* Nav */}
      <nav className="flex justify-between items-center px-8 py-4 bg-white border-b border-gray-100">
        <Link to="/" className="flex items-center gap-3">
          <svg width="40" height="40" viewBox="0 0 90 90" style={{borderRadius: '10px', display: 'block'}}>
            <rect width="90" height="90" rx="22" fill="#046645"/>
            <path d="M22 45 C22 28, 68 28, 68 45 L22 45 Z" fill="white"/>
            <rect x="22" y="41" width="46" height="5" fill="#046645"/>
            <path d="M22 45 C22 65, 45 72, 62 65" stroke="white" strokeWidth="5.5" strokeLinecap="round" fill="none"/>
            <circle cx="68" cy="32" r="6" fill="white" fillOpacity="0.2"/>
          </svg>
          <span className="font-grotesk font-semibold text-brand" style={{fontSize: '24px', letterSpacing: 0}}>
            ELUCIDE
          </span>
        </Link>

        {isLoggedIn && (
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-6">
              <Link
                to="/word-input"
                className="text-sm text-lattice-dark hover:text-lattice-green transition-colors"
              >
                Homepage
              </Link>
              <Link
                to="/quiz"
                className="text-sm text-lattice-dark hover:text-lattice-green transition-colors"
              >
                Quiz Mode
              </Link>
            </div>
            <button
              onClick={logout}
              className="px-4 py-2 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text text-sm font-medium rounded-lg transition-colors"
            >
              Logout
            </button>
          </div>
        )}
      </nav>

      <Routes>
        <Route
          path="/"
          element={isLoggedIn ? <Navigate to="/word-input" /> : <AuthForm />}
        />
        <Route
          path="/word-input"
          element={
            isLoggedIn ? (
              <div className="max-w-4xl mx-auto px-4 py-8">
                <WordInput addWord={addWord} getMeaning={getMeaning} userEmail={userEmail} userId={userId} />
              </div>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route
          path="/quiz"
          element={
            isLoggedIn ? (
              <div className="max-w-4xl mx-auto px-4 py-8">
                <QuizMode userId={userId} />
              </div>
            ) : (
              <Navigate to="/" />
            )
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
      </Routes>
    </div>
  );
}

export default App;
